import React, { useRef, useEffect, useState, useContext } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import io from "socket.io-client";
import { AuthContext } from "../../../context/AuthContext";
import { useDbContext } from "../../../context/DbContext";
import { selectActiveFyid, selectActiveFy } from "../../../app/financialYearSlice";
import { selectActiveLocation } from "../../../app/locationSlice";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";

// ─── Sub-components defined OUTSIDE main function to prevent remount on re-render ───

function Field({ label, children }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-[10px] w-full">
      <label className="text-[11px] font-bold text-slate-700 font-['Inter'] text-left whitespace-nowrap">
        {label}
      </label>
      <div className="w-full">{children}</div>
    </div>
  );
}

function DateRow({ fromDate, toDate, setFromDate, setToDate, minDate, maxDate, isDateInFy }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-[10px]">
      <label className="text-[11px] font-bold text-slate-700 font-['Inter']">
        Period
      </label>
      <div className="grid grid-cols-2 gap-[10px]">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400 font-semibold font-['Inter'] whitespace-nowrap">
            From
          </span>
          <input
            type="date"
            min={minDate}
            max={maxDate}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            onBlur={(e) => {
              const val = e.target.value;
              if (val && !isDateInFy(val)) {
                alert("Date incompatible with financial year");
              }
            }}
            className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400 font-semibold font-['Inter'] whitespace-nowrap">
            To
          </span>
          <input
            type="date"
            min={minDate}
            max={maxDate}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            onBlur={(e) => {
              const val = e.target.value;
              if (val && !isDateInFy(val)) {
                alert("Date incompatible with financial year");
              }
            }}
            className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
          />
        </div>
      </div>
    </div>
  );
}

function RadioRow({ label, name, options, value, onChange }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-[10px]">
      <label className="text-[11px] font-bold text-slate-700 font-['Inter']">
        {label}
      </label>
      <div className="flex items-center gap-5">
        {options.map(([val, lbl]) => (
          <label
            key={val}
            className="flex items-center gap-1.5 cursor-pointer select-none"
          >
            <input
              type="radio"
              name={name}
              value={val}
              checked={value === val}
              onChange={onChange}
              className="w-3.5 h-3.5 accent-[#0f56d3] cursor-pointer"
            />
            <span className="text-[11px] font-semibold text-slate-500 font-['Inter']">
              {lbl}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function FooterButtons({ onShow, onClose }) {
  return (
    <div className="flex justify-end items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2.5">
      <button
        onClick={onShow}
        className="flex items-center gap-1.5 px-4 h-[30px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm text-[11px] font-bold text-[#0f56d3] font-['Inter']"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#2E7D32"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Show Report
      </button>
      <button
        onClick={onClose}
        className="flex items-center gap-1.5 px-4 h-[30px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm text-[11px] font-bold text-slate-600 font-['Inter']"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#D32F2F"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
        Exit
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

function LedgerRepModel({ onClose, openReport, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { agentCode } = useContext(AuthContext);
  const { dbCode } = useDbContext();
  // ── Financial Year (from global Redux store, same as other entry forms) ──
  const activeFyid = useSelector(selectActiveFyid);
  const activeFy = useSelector(selectActiveFy);
  const fyid = activeFyid || Number(sessionStorage.getItem("selectedFyId")) || Number(localStorage.getItem("selectedFyId")) || 0;
  const activeLocation = useSelector(selectActiveLocation);
  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  const storedLocId = sessionStorage.getItem("loginLocationId") || localStorage.getItem("loginLocationId");
  const storedLocName = sessionStorage.getItem("loginLocation") || localStorage.getItem("loginLocation");
  const defaultLocId = activeLocation?.id || storedLocId;
  const defaultLocName = activeLocation?.name || storedLocName;

  const orderbyref = useRef(null);
  const locationRef = useRef(null);
  const reporttyperef = useRef(null);
  const ledgerRef = useRef(null);
  const customRptRef = useRef(null);
  const groupRef = useRef(null);

  // ── Date helper ──────────────────────────────────────────────────────────
  const getCurrentDate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const today = getCurrentDate();

  // ── Form State ────────────────────────────────────────────────────────────
  const [ledgerRepData, setledgerRepData] = useState({
    fromDate: minDate || today,
    toDate:   (maxDate && today > maxDate) ? maxDate : today,
    orderBy: { value: "", label: "" },
    ledger: { value: "", label: "" },
    location: defaultLocId
      ? { value: String(defaultLocId), label: defaultLocName || "SHOP" }
      : { value: "0", label: "All Locations" },
    reportType: { value: 1, label: "Summary" },
    openingBalance: false,
    simpleLedgerReport: false,
    optionType: "Summary",
    showQty: "No",
    showCostCenter: "No",
    customReportType: { value: "", label: "" },
    costCenter: { value: "", label: "" },
    Group: { value: "", label: "" },
    fyid: fyid,
  });

  // Sync financial year date limits when minDate/maxDate become available from Redux
  useEffect(() => {
    if (minDate) {
      setledgerRepData((prev) => ({
        ...prev,
        fromDate: (!prev.fromDate || prev.fromDate === today) ? minDate : prev.fromDate,
        toDate: prev.toDate || ((maxDate && today > maxDate) ? maxDate : today),
        fyid: fyid || prev.fyid,
      }));
    }
  }, [minDate, maxDate, today, fyid]);

  const hasAlertedFyRef = useRef(false);
  useEffect(() => {
    if (activeFy && !hasAlertedFyRef.current) {
      if (!isDateInFy(today)) {
        alert("Current date is outside the active financial year");
        hasAlertedFyRef.current = true;
      }
    }
  }, [activeFy, today, isDateInFy]);

  const [activeTab, setActiveTab] = useState("tab1");

  // Radio local state
  const [selOptType, setSelOptType] = useState(1);
  const [selShowQty, setSelShowQty] = useState(1);
  const [selShowCostCenter, setSelShowCostCenter] = useState(1);

  const radioOpts = [
    [1, "Summary"],
    [2, "Detailed"],
  ];
  const radioOptsYesNo = [
    [1, "No"],
    [2, "Yes"],
  ];

  // ── Data ──────────────────────────────────────────────────────────────────
  const [LedgerName, setLedgerName] = useState([]);
  const [under, setUnder] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  useEffect(() => {
    const fetchLgname = async () => {
      try {
        const res = await axios.get(
          `${apiBaseUrl}/main/jvledgernames/${dbCode}`,
        );
        setLedgerName(
          res.data.map((item) => ({
            value: item.Ledcode,
            label: item.LedName,
          })),
        );
      } catch (err) {
        console.error("Error fetching ledger names:", err.message);
      }
    };

    const fetchLocation = async () => {
      try {
        const res = await axios.get(
          `${apiBaseUrl}/otherregistration/getoptions/${dbCode}`,
        );
        const locations = (res.data || []).filter(
          (rec) => String(rec.Type || rec.type || "").toLowerCase() === "location",
        );
        const mapped = locations.map((rec) => ({
          value: String(rec.Auto ?? rec.auto ?? rec.SlNo ?? rec.slno ?? ""),
          label: rec.Name || rec.name || "",
        }));
        const opts = [{ value: "0", label: "All Locations" }, ...mapped];
        setLocationOptions(opts);

        // Pre-select active logged-in location
        if (opts.length > 0) {
          const targetId = activeLocation?.id || storedLocId;
          const match = targetId ? opts.find((opt) => Number(opt.value) === Number(targetId)) : null;
          if (match) {
            setledgerRepData((prev) => ({ ...prev, location: match }));
          }
        }
      } catch (err) {
        console.error("Error fetching location options:", err.message);
      }
    };

    fetchLgname();
    fetchLocation();

    const socket = io(apiBaseUrl);
    socket.on("dataUpdated", fetchLgname);
    socket.on("OtherRegistrationUpdated", fetchLocation);
    return () => socket.disconnect();
  }, [apiBaseUrl, dbCode, activeLocation]);

  useEffect(() => {
    const fetchLedgerHeads = async () => {
      try {
        const res = await axios.get(
          `${apiBaseUrl}/main/spledgerheads/${dbCode}`,
        );
        setUnder(
          res.data.map((item) => ({ value: item.lh_id, label: item.lh_name })),
        );
      } catch (err) {
        console.error("Error fetching ledger heads:", err.message);
      }
    };
    fetchLedgerHeads();
  }, [apiBaseUrl, dbCode]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // ── Static Options ────────────────────────────────────────────────────────
  const reportTypeOptions = [
    { value: 1, label: "Summary" },
    { value: 2, label: "Pending Bills" },
    { value: 3, label: "Daily" },
    { value: 4, label: "All Ledger" },
    { value: 5, label: "All Time Ledger" },
    { value: 6, label: "Receipt Wise Ledger Report" },
    { value: 7, label: "FxLedger Report" },
    { value: 8, label: "Ecom Agent Ledger Report" },
  ];
  // branchOptions is now fetched dynamically from OtherRegistration (type = "Location")
  const customRptOptions = [
    { value: 1, label: "Cash Flow" },
    { value: 2, label: "Fund Flow" },
    { value: 3, label: "Cash Book Projection" },
  ];

  // ── Handlers ──────────────────────────────────────────────────────────────
  const set = (key, val) => {
    if (typeof val === "function") {
      setledgerRepData((prev) => ({ ...prev, [key]: val(prev[key]) }));
    } else {
      setledgerRepData((prev) => ({ ...prev, [key]: val }));
    }
  };

  const handleOptType = (e) => {
    const v = parseInt(e.target.value);
    setSelOptType(v);
    set("optionType", v === 1 ? "Summary" : "Detailed");
  };
  const handleShowQty = (e) => {
    const v = parseInt(e.target.value);
    setSelShowQty(v);
    set("showQty", v === 1 ? "No" : "Yes");
  };
  const handleShowCC = (e) => {
    const v = parseInt(e.target.value);
    setSelShowCostCenter(v);
    set("showCostCenter", v === 1 ? "No" : "Yes");
  };

  const tabs = ["Ledger", "Options", "Custom", "Cost Center", "Group"];

  return (
    <div
      className="absolute inset-0 w-full h-full bg-transparent flex items-center justify-center pointer-events-auto font-['Inter']"
      style={{ zIndex: zIndex || 101 }}
    >
      <div className="bg-white w-[640px] rounded-xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden font-['Inter']">
        {/* ── Header ── */}
        <WindowHeader
          title="Ledger Report"
          logo={headeLogo}
          onClose={onClose}
          className="bg-[#0f56d3] text-white"
          closeBtnClassName="w-6 h-6 border-none rounded-[5px] mr-2 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white font-bold"
        />

        {/* ── Tab Strip ── */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-4 gap-1 pt-2">
          {tabs.map((tab, idx) => {
            const tabId = `tab${idx + 1}`;
            const isActive = activeTab === tabId;
            return (
              <button
                key={tabId}
                type="button"
                onClick={() => setActiveTab(tabId)}
                className={[
                  "px-4 py-[6px] text-[11px] font-bold font-['Inter'] cursor-pointer transition-all duration-150 rounded-t-md border-b-2",
                  isActive
                    ? "text-[#0f56d3] border-[#0f56d3] bg-white -mb-px"
                    : "text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-100/60",
                ].join(" ")}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* ── Form Area ── */}
        <div className="px-4 pt-3 pb-2 flex flex-col gap-[10px] min-h-[230px] font-['Inter']">
          {/* Date row — always visible */}
          <DateRow
            fromDate={ledgerRepData.fromDate}
            toDate={ledgerRepData.toDate}
            setFromDate={(v) => set("fromDate", v)}
            setToDate={(v) => set("toDate", v)}
            minDate={minDate}
            maxDate={maxDate}
            isDateInFy={isDateInFy}
          />

          {/* ── TAB 1: Ledger ── */}
          {activeTab === "tab1" && (
            <>
              <Field label="Order By">
                <SherComboBox
                  comboRef={orderbyref}
                  options={[]}
                  value={
                    ledgerRepData.orderBy?.value ||
                    ledgerRepData.orderBy?.label ||
                    ""
                  }
                  onSelect={(opt) =>
                    set(
                      "orderBy",
                      opt
                        ? { value: opt.value, label: opt.label }
                        : { value: "", label: "" },
                    )
                  }
                  onInputChange={(text) =>
                    set("orderBy", (prev) =>
                      prev?.label === text ? prev : { value: "", label: text },
                    )
                  }
                  showArrow
                  clearable={false}
                  inputWidth="100%"
                  inputHeight="28px"
                  inputClassName="border border-slate-300 rounded focus:border-blue-500 text-[11px] font-['Inter'] font-semibold text-slate-900"
                />
              </Field>

              <Field label="Select Ledger">
                <SherComboBox
                  comboRef={ledgerRef}
                  options={LedgerName}
                  value={
                    ledgerRepData.ledger?.label ||
                    ledgerRepData.ledger?.value ||
                    ""
                  }
                  onSelect={(opt) =>
                    set(
                      "ledger",
                      opt
                        ? { value: opt.value, label: opt.label }
                        : { value: "", label: "" },
                    )
                  }
                  onInputChange={(text) => {
                    const match = LedgerName.find(
                      (l) => l.label?.trim().toLowerCase() === text?.trim().toLowerCase()
                    );
                    set("ledger", match || { value: "", label: text });
                  }}
                  placeholder="Search ledger..."
                  showArrow
                  clearable
                  inputWidth="100%"
                  inputHeight="28px"
                  inputClassName="border border-slate-300 rounded focus:border-blue-500 text-[11px] font-['Inter'] font-semibold text-slate-900"
                />
              </Field>

              <Field label="Location">
                <SherComboBox
                  comboRef={locationRef}
                  options={locationOptions}
                  value={
                    ledgerRepData.location?.value ||
                    ledgerRepData.location?.label ||
                    ""
                  }
                  onSelect={(opt) =>
                    set(
                      "location",
                      opt
                        ? { value: opt.value, label: opt.label }
                        : { value: "", label: "" },
                    )
                  }
                  onInputChange={(text) =>
                    set("location", (prev) =>
                      prev?.label === text ? prev : { value: "", label: text },
                    )
                  }
                  showArrow
                  clearable={false}
                  inputWidth="100%"
                  inputHeight="28px"
                  inputClassName="border border-slate-300 rounded focus:border-blue-500 text-[11px] font-['Inter'] font-semibold text-slate-900"
                />
              </Field>

              <Field label="Report Type">
                <SherComboBox
                  comboRef={reporttyperef}
                  options={reportTypeOptions}
                  value={
                    ledgerRepData.reportType?.value ||
                    ledgerRepData.reportType?.label ||
                    ""
                  }
                  onSelect={(opt) =>
                    set(
                      "reportType",
                      opt
                        ? { value: opt.value, label: opt.label }
                        : { value: "", label: "" },
                    )
                  }
                  onInputChange={(text) =>
                    set("reportType", (prev) =>
                      prev?.label === text ? prev : { value: "", label: text },
                    )
                  }
                  showArrow
                  clearable={false}
                  inputWidth="100%"
                  inputHeight="28px"
                  inputClassName="border border-slate-300 rounded focus:border-blue-500 text-[11px] font-['Inter'] font-semibold text-slate-900"
                />
              </Field>
            </>
          )}

          {/* ── TAB 2: Options ── */}
          {activeTab === "tab2" && (
            <>
              <div className="grid grid-cols-[110px_1fr] items-center gap-[10px]">
                <label className="text-[11px] font-bold text-slate-700 font-['Inter']">
                  Balances
                </label>
                <div className="flex items-center gap-5">
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!ledgerRepData.openingBalance}
                      onChange={(e) => set("openingBalance", e.target.checked)}
                      className="w-3.5 h-3.5 accent-[#0f56d3] cursor-pointer"
                    />
                    <span className="text-[11px] font-bold text-slate-700 font-['Inter']">
                      Opening Balance
                    </span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!ledgerRepData.simpleLedgerReport}
                      onChange={(e) =>
                        set("simpleLedgerReport", e.target.checked)
                      }
                      className="w-3.5 h-3.5 accent-[#0f56d3] cursor-pointer"
                    />
                    <span className="text-[11px] font-bold text-slate-700 font-['Inter']">
                      Simple Ledger Report
                    </span>
                  </label>
                </div>
              </div>

              <RadioRow
                label="Type"
                name="optionType"
                options={radioOpts}
                value={selOptType}
                onChange={handleOptType}
              />
              <RadioRow
                label="Show Qty"
                name="showQty"
                options={radioOptsYesNo}
                value={selShowQty}
                onChange={handleShowQty}
              />
              <RadioRow
                label="Show Cost Center"
                name="showCostCenter"
                options={radioOptsYesNo}
                value={selShowCostCenter}
                onChange={handleShowCC}
              />
            </>
          )}

          {/* ── TAB 3: Custom ── */}
          {activeTab === "tab3" && (
            <Field label="Report Type">
              <SherComboBox
                comboRef={customRptRef}
                options={customRptOptions}
                value={
                  ledgerRepData.customReportType?.value ||
                  ledgerRepData.customReportType?.label ||
                  ""
                }
                onSelect={(opt) =>
                  set(
                    "customReportType",
                    opt
                      ? { value: opt.value, label: opt.label }
                      : { value: "", label: "" },
                  )
                }
                onInputChange={(text) =>
                  set("customReportType", (prev) =>
                    prev?.label === text ? prev : { value: "", label: text },
                  )
                }
                showArrow
                clearable={false}
                inputWidth="100%"
                inputHeight="28px"
                inputClassName="border border-slate-300 rounded focus:border-blue-500 text-[11px] font-['Inter'] font-semibold text-slate-900"
              />
            </Field>
          )}

          {/* ── TAB 4: Cost Center ── */}
          {activeTab === "tab4" && (
            <Field label="Cost Center">
              <SherComboBox
                options={[]}
                value={
                  ledgerRepData.costCenter?.value ||
                  ledgerRepData.costCenter?.label ||
                  ""
                }
                onSelect={(opt) =>
                  set(
                    "costCenter",
                    opt
                      ? { value: opt.value, label: opt.label }
                      : { value: "", label: "" },
                  )
                }
                onInputChange={(text) =>
                  set("costCenter", (prev) =>
                    prev?.label === text ? prev : { value: "", label: text },
                  )
                }
                placeholder="Select cost center..."
                showArrow
                clearable
                inputWidth="100%"
                inputHeight="28px"
                inputClassName="border border-slate-300 rounded focus:border-blue-500 text-[11px] font-['Inter'] font-semibold text-slate-900"
              />
            </Field>
          )}

          {/* ── TAB 5: Group ── */}
          {activeTab === "tab5" && (
            <Field label="Group">
              <SherComboBox
                comboRef={groupRef}
                options={under}
                value={
                  ledgerRepData.Group?.value || ledgerRepData.Group?.label || ""
                }
                onSelect={(opt) =>
                  set(
                    "Group",
                    opt
                      ? { value: opt.value, label: opt.label }
                      : { value: "", label: "" },
                  )
                }
                onInputChange={(text) =>
                  set("Group", (prev) =>
                    prev?.label === text ? prev : { value: "", label: text },
                  )
                }
                placeholder="Search group..."
                showArrow
                clearable
                inputWidth="100%"
                inputHeight="28px"
                inputClassName="border border-slate-300 rounded focus:border-blue-500 text-[11px] font-['Inter'] font-semibold text-slate-900"
              />
            </Field>
          )}
        </div>

        {/* ── Footer ── */}
        {/* fyid is automatically taken from the global Redux active FY */}
        <FooterButtons
          onShow={() => {
            if (ledgerRepData.fromDate && !isDateInFy(ledgerRepData.fromDate)) {
              alert("From date incompatible with financial year");
              return;
            }
            if (ledgerRepData.toDate && !isDateInFy(ledgerRepData.toDate)) {
              alert("To date incompatible with financial year");
              return;
            }

            // Resolve ledger in case user typed/pasted without selecting from dropdown
            let resolvedLedger = ledgerRepData.ledger;
            if (resolvedLedger?.label && (!resolvedLedger.value || isNaN(parseInt(resolvedLedger.value, 10)))) {
              const match = LedgerName.find(
                (l) => l.label?.trim().toLowerCase() === resolvedLedger.label.trim().toLowerCase()
              );
              if (match) {
                resolvedLedger = match;
              }
            }

            openReport({
              ...ledgerRepData,
              ledger: resolvedLedger,
              fyid: fyid || ledgerRepData.fyid || 0,
            });
          }}
          onClose={onClose}
        />
      </div>
    </div>
  );
}

export default LedgerRepModel;
